import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, Calendar, Car, Users, Accessibility, Building2, 
  Plane, Zap, Battery, KeyRound, Package, ChevronRight, 
  CheckCircle2, CreditCard
} from 'lucide-react';

const ServicesPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      metaTitle: "Services de Taxi à Montréal — Berline, Minivan, Accessible, Corporatif | MTL Taxi",
      metaDesc: "MTL Taxi offre berline standard, minivan 6 passagers, taxi accessible fauteuil roulant, comptes corporatifs, navette aéroport YUL et services express. Disponible 24h/24 — 438-600-4440.",
      title: "Nos services de taxi à Montréal — Transport pour tous vos besoins",
      intro: "MTL Taxi offre une gamme complète de services de transport à Montréal et dans le Grand Montréal. Disponibles 24 heures sur 24, 7 jours sur 7, nos chauffeurs professionnels vous conduisent où vous en avez besoin — en toute sécurité et à tarif réglementé.",
      
      // Berline
      sedanTitle: "Berline Standard",
      sedanDesc: "Notre berline standard est le choix idéal pour vos déplacements quotidiens à Montréal. Confortable, propre et économique, elle accueille jusqu'à 4 passagers pour tout trajet en ville, vers la banlieue ou vers l'aéroport.",
      sedanFeatures: [
        "Jusqu'à 4 passagers",
        "Confortable et économique",
        "Idéal pour les déplacements quotidiens, rendez-vous médicaux, sorties",
        "Paiement par carte ou comptant accepté"
      ],
      sedanLinks: "Voir les tarifs",

      // Minivan
      minivanTitle: "Taxi Minivan — 6 Passagers",
      minivanDesc: "Voyagez en groupe sans vous séparer. Notre minivan accueille jusqu'à 6 passagers avec leurs bagages — parfait pour les familles, les groupes d'amis ou les équipes en déplacement professionnel. Un seul véhicule, un seul tarif, plus économique que deux taxis séparés.",
      minivanFeatures: [
        "Jusqu'à 6 passagers",
        "Grand espace pour bagages volumineux",
        "Idéal pour les familles et les groupes",
        "Disponible pour navette aéroport YUL",
        "Supplément minivan : 10 $"
      ],

      // Accessible
      accessibleTitle: "Taxi Accessible — Fauteuil Roulant",
      accessibleDesc: "MTL Taxi met à votre disposition des véhicules adaptés pour les personnes à mobilité réduite. Nos chauffeurs sont formés pour assister les passagers en fauteuil roulant avec respect et professionnalisme. La réservation à l'avance est recommandée pour garantir la disponibilité d'un véhicule adapté.",
      accessibleFeatures: [
        "Rampes d'accès pour fauteuils roulants",
        "Chauffeurs formés et attentionnés",
        "Disponible 24h/24 sur tout le Grand Montréal",
        "Réservation à l'avance recommandée"
      ],

      // Corporate
      corporateTitle: "Comptes Corporatifs",
      corporateDesc: "Simplifiez la gestion des déplacements de votre entreprise. MTL Taxi offre des comptes corporatifs avec facturation mensuelle centralisée, rapports de dépenses détaillés et service prioritaire. Idéal pour les transferts clients, les déplacements d'employés et les navettes vers l'aéroport YUL.",
      corporateFeatures: [
        "Facturation mensuelle centralisée",
        "Rapports de dépenses détaillés pour la comptabilité",
        "Tarifs négociés selon le volume",
        "Service prioritaire 24h/24",
        "Idéal pour navettes vers l'aéroport"
      ],
      corporateContact: "Nous contacter pour un compte corporatif",

      // Airport
      airportTitle: "Navette Aéroport Montréal-Trudeau (YUL)",
      airportDesc: "MTL Taxi assure les transferts vers et depuis l'aéroport international Montréal-Trudeau 24h/24. Tarif fixe pour le centre-ville, suivi de vol inclus, minivan disponible pour les groupes. Réservez à l'avance pour un départ sans stress.",
      airportFeatures: [
        "Tarif fixe centre-ville ↔ YUL : 49,45 $ (jour) / 56,70 $ (nuit)",
        "Suivi de vol — nous attendons même si votre vol est retardé",
        "Minivan disponible pour les groupes avec bagages",
        "Service 24h/24, 365 jours par an"
      ],
      airportLink: "Voir tous les tarifs aéroport",

      // Express Services
      expressTitle: "Services Express",
      expressDesc: "MTL Taxi offre trois services express disponibles rapidement sur simple appel.",
      batteryTitle: "Survoltage de batterie — 25 $",
      batteryDesc: "Votre batterie est à plat ? Nous envoyons un chauffeur équipé pour recharger votre véhicule sur place, à Montréal et dans le Grand Montréal.",
      unlockTitle: "Déverrouillage de porte — 30 $",
      unlockDesc: "Clés oubliées à l'intérieur de votre voiture ? Nos chauffeurs peuvent intervenir rapidement pour déverrouiller votre véhicule.",
      deliveryTitle: "Livraison de colis — 6,50 $ + taximètre",
      deliveryDesc: "Besoin d'envoyer un colis rapidement à Montréal ? Nous le récupérons et le livrons à destination dans les meilleurs délais.",
      expressCallText: "Pour tous les services express :",

      // Payment
      paymentTitle: "Modes de paiement acceptés",
      paymentIntro: "Tous nos chauffeurs acceptent :",
      paymentMethods: [
        "Comptant",
        "Carte de débit Interac",
        "Visa, Mastercard, American Express",
        "Facturation mensuelle pour les comptes corporatifs"
      ],

      // CTA
      ctaTitle: "Réservez votre taxi maintenant",
      ctaText: "Disponible 24h/24 — appelez directement ou réservez en ligne.",
      callBtn: "Appeler 438-600-4440",
      bookBtn: "Réserver en ligne",

      // Footer
      footerHome: "← Accueil",
      footerAirport: "Aéroport YUL",
      footerFares: "Tarifs",
      footerZones: "Zones desservies",
      footerFaq: "FAQ",
      footerContact: "Contact"
    },
    en: {
      metaTitle: "Taxi Services in Montreal — Sedan, Minivan, Accessible, Corporate | MTL Taxi",
      metaDesc: "MTL Taxi offers standard sedan, 6-passenger minivan, wheelchair accessible taxi, corporate accounts, YUL airport shuttle and express services. Available 24/7 — 438-600-4440.",
      title: "Our Taxi Services in Montreal — Transportation for All Your Needs",
      intro: "MTL Taxi offers a complete range of transportation services in Montreal and Greater Montreal. Available 24 hours a day, 7 days a week, our professional drivers take you where you need to go — safely and at regulated rates.",
      
      sedanTitle: "Standard Sedan",
      sedanDesc: "Our standard sedan is the ideal choice for your daily trips in Montreal. Comfortable, clean and economical, it accommodates up to 4 passengers for any trip in the city, to the suburbs or to the airport.",
      sedanFeatures: [
        "Up to 4 passengers",
        "Comfortable and economical",
        "Ideal for daily trips, medical appointments, outings",
        "Card or cash payment accepted"
      ],
      sedanLinks: "View rates",

      minivanTitle: "Minivan Taxi — 6 Passengers",
      minivanDesc: "Travel as a group without splitting up. Our minivan accommodates up to 6 passengers with their luggage — perfect for families, groups of friends or teams on business trips. One vehicle, one fare, more economical than two separate taxis.",
      minivanFeatures: [
        "Up to 6 passengers",
        "Large space for bulky luggage",
        "Ideal for families and groups",
        "Available for YUL airport shuttle",
        "Minivan supplement: $10"
      ],

      accessibleTitle: "Accessible Taxi — Wheelchair",
      accessibleDesc: "MTL Taxi provides adapted vehicles for people with reduced mobility. Our drivers are trained to assist wheelchair passengers with respect and professionalism. Advance booking is recommended to ensure availability of an adapted vehicle.",
      accessibleFeatures: [
        "Wheelchair access ramps",
        "Trained and attentive drivers",
        "Available 24/7 throughout Greater Montreal",
        "Advance booking recommended"
      ],

      corporateTitle: "Corporate Accounts",
      corporateDesc: "Simplify the management of your company's travel. MTL Taxi offers corporate accounts with centralized monthly billing, detailed expense reports and priority service. Ideal for client transfers, employee travel and airport shuttles to YUL.",
      corporateFeatures: [
        "Centralized monthly billing",
        "Detailed expense reports for accounting",
        "Negotiated rates based on volume",
        "Priority service 24/7",
        "Ideal for airport shuttles"
      ],
      corporateContact: "Contact us for a corporate account",

      airportTitle: "Montreal-Trudeau Airport Shuttle (YUL)",
      airportDesc: "MTL Taxi provides transfers to and from Montreal-Trudeau International Airport 24/7. Fixed rate for downtown, flight tracking included, minivan available for groups. Book in advance for a stress-free departure.",
      airportFeatures: [
        "Fixed rate downtown ↔ YUL: $49.45 (day) / $56.70 (night)",
        "Flight tracking — we wait even if your flight is delayed",
        "Minivan available for groups with luggage",
        "24/7 service, 365 days a year"
      ],
      airportLink: "View all airport rates",

      expressTitle: "Express Services",
      expressDesc: "MTL Taxi offers three express services available quickly on a simple call.",
      batteryTitle: "Battery Boost — $25",
      batteryDesc: "Battery dead? We send an equipped driver to recharge your vehicle on site, in Montreal and Greater Montreal.",
      unlockTitle: "Door Unlock — $30",
      unlockDesc: "Keys forgotten inside your car? Our drivers can quickly intervene to unlock your vehicle.",
      deliveryTitle: "Parcel Delivery — $6.50 + meter",
      deliveryDesc: "Need to send a parcel quickly in Montreal? We pick it up and deliver it to its destination as soon as possible.",
      expressCallText: "For all express services:",

      paymentTitle: "Accepted Payment Methods",
      paymentIntro: "All our drivers accept:",
      paymentMethods: [
        "Cash",
        "Interac debit card",
        "Visa, Mastercard, American Express",
        "Monthly billing for corporate accounts"
      ],

      ctaTitle: "Book Your Taxi Now",
      ctaText: "Available 24/7 — call directly or book online.",
      callBtn: "Call 438-600-4440",
      bookBtn: "Book online",

      footerHome: "← Home",
      footerAirport: "YUL Airport",
      footerFares: "Rates",
      footerZones: "Service Areas",
      footerFaq: "FAQ",
      footerContact: "Contact"
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <SEO 
        title={t.metaTitle}
        description={t.metaDesc}
        canonical={`https://mtl.taxi/${language === 'fr' ? 'fr/services' : 'en/services'}`}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight leading-tight">
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </div>
      </section>

      {/* Berline Standard */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-blue-50 text-primary flex items-center justify-center flex-shrink-0">
              <Car className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                {t.sedanTitle}
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground text-lg mb-6">{t.sedanDesc}</p>
          <ul className="space-y-3 mb-6">
            {t.sedanFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4">
            <Link to={getRoute('/fares')} className="text-primary font-semibold hover:underline">
              → {t.sedanLinks}
            </Link>
            <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
              → {language === 'fr' ? 'Réserver en ligne' : 'Book online'}
            </a>
          </div>
        </div>
      </section>

      {/* Minivan */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                {t.minivanTitle}
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground text-lg mb-6">{t.minivanDesc}</p>
          <ul className="space-y-3 mb-6">
            {t.minivanFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span>
                  {feature}
                  {feature.includes('aéroport') || feature.includes('airport') ? (
                    <Link to={getRoute('/airport')} className="text-primary ml-1 hover:underline">→ /aeroport</Link>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4">
            <Link to={getRoute('/fares')} className="text-primary font-semibold hover:underline">
              → {t.sedanLinks}
            </Link>
            <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
              → {language === 'fr' ? 'Réserver en ligne' : 'Book online'}
            </a>
          </div>
        </div>
      </section>

      {/* Accessible */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
              <Accessibility className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                {t.accessibleTitle}
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground text-lg mb-6">{t.accessibleDesc}</p>
          <ul className="space-y-3 mb-6">
            {t.accessibleFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <a href={siteConfig.phoneTel} className="text-primary font-semibold hover:underline">
            → {t.callBtn}
          </a>
        </div>
      </section>

      {/* Corporate */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                {t.corporateTitle}
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground text-lg mb-6">{t.corporateDesc}</p>
          <ul className="space-y-3 mb-6">
            {t.corporateFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span>
                  {feature}
                  {feature.includes('aéroport') || feature.includes('airport') ? (
                    <Link to={getRoute('/airport')} className="text-primary ml-1 hover:underline">→ /aeroport</Link>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4">
            <Link to={getRoute('/contact')} className="text-primary font-semibold hover:underline">
              → {t.corporateContact}
            </Link>
            <a href={siteConfig.phoneTel} className="text-primary font-semibold hover:underline">
              → {t.callBtn}
            </a>
          </div>
        </div>
      </section>

      {/* Airport */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
              <Plane className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
                {t.airportTitle}
              </h2>
            </div>
          </div>
          <p className="text-muted-foreground text-lg mb-6">{t.airportDesc}</p>
          <ul className="space-y-3 mb-6">
            {t.airportFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground">
                <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4">
            <Link to={getRoute('/airport')} className="text-primary font-semibold hover:underline">
              → {t.airportLink}
            </Link>
            <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
              → {language === 'fr' ? 'Réserver en ligne' : 'Book online'}
            </a>
          </div>
        </div>
      </section>

      {/* Express Services */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
              <Zap className="w-7 h-7" />
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
              {t.expressTitle}
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-8">{t.expressDesc}</p>

          <div className="space-y-6">
            {/* Battery */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Battery className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground text-lg mb-2">{t.batteryTitle}</h3>
                    <p className="text-muted-foreground">{t.batteryDesc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Unlock */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <KeyRound className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground text-lg mb-2">{t.unlockTitle}</h3>
                    <p className="text-muted-foreground">{t.unlockDesc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delivery */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Package className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground text-lg mb-2">{t.deliveryTitle}</h3>
                    <p className="text-muted-foreground">{t.deliveryDesc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <p className="text-foreground mt-6">
            {t.expressCallText} <a href={siteConfig.phoneTel} className="text-primary font-semibold hover:underline">{t.callBtn}</a>
          </p>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.paymentTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-6">{t.paymentIntro}</p>
          <ul className="space-y-3">
            {t.paymentMethods.map((method, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground text-lg">
                <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
                <span>
                  {method}
                  {method.includes('corporatif') || method.includes('corporate') ? (
                    <Link to={getRoute('/contact')} className="text-primary ml-1 hover:underline">
                      → /{language === 'fr' ? 'corporatif' : 'corporate'}
                    </Link>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-accent-foreground/80 text-lg mb-8">
            {t.ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t.bookBtn}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-8 bg-muted/50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
            <Link to={getRoute('/')} className="text-muted-foreground hover:text-primary">{t.footerHome}</Link>
            <Link to={getRoute('/airport')} className="text-muted-foreground hover:text-primary">{t.footerAirport}</Link>
            <Link to={getRoute('/fares')} className="text-muted-foreground hover:text-primary">{t.footerFares}</Link>
            <Link to={getRoute('/zones')} className="text-muted-foreground hover:text-primary">{t.footerZones}</Link>
            <Link to={getRoute('/faq')} className="text-muted-foreground hover:text-primary">{t.footerFaq}</Link>
            <Link to={getRoute('/contact')} className="text-muted-foreground hover:text-primary">{t.footerContact}</Link>
          </nav>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
